import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Play, Hand, Check } from "lucide-react";
import { getSongById } from "@/data/demoSongs";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n/I18nContext";
import { Fretboard } from "@/components/jam/Fretboard";
import { ModeSelector } from "@/components/jam/ModeSelector";
import { MicVisualizer } from "@/components/jam/MicVisualizer";
import { playFretNote } from "@/audio/synth";

export default function Prepare() {
  const { songId } = useParams();
  const navigate = useNavigate();
  const { mode, setMode } = useApp();
  const { t, tm, dict } = useI18n();
  const song = getSongById(songId || "");

  if (!song) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0C0E] text-slate-300">
        <button onClick={() => navigate("/library")} className="underline">
          {t("nav.library")}
        </button>
      </div>
    );
  }

  const zoneA = song.zones[Object.keys(song.zones)[0]];
  const needs = dict.songs[song.id]?.needs ?? song.needs;

  return (
    <div className="min-h-[100dvh] bg-[#0A0C0E] text-slate-200">
      <div className="mx-auto w-full max-w-2xl px-5 py-8">
        <button
          onClick={() => navigate("/library")}
          className="flex items-center gap-1 text-slate-400 transition hover:text-slate-200"
        >
          <ChevronLeft size={18} /> <span className="text-sm">{t("nav.library")}</span>
        </button>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-6"
        >
          <span className="font-mono text-[11px] uppercase tracking-[0.4em] text-amber-400/90">
            {t("prepare.kicker", { style: tm("styles", song.style) })}
          </span>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-100">{song.title}</h1>
          <div className="mt-1 font-mono text-xs text-slate-500">
            {t("prepare.meta", {
              key: tm("keys", song.key),
              bpm: song.bpm,
              a: song.timeSignature[0],
              b: song.timeSignature[1],
            })}
          </div>
        </motion.div>

        {/* This song needs */}
        <div className="mt-8">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-400">
            {t("prepare.needs")}
          </h2>
          <div className="mt-3 space-y-2">
            {needs.map((need, i) => (
              <motion.div
                key={need}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + i * 0.08 }}
                className="flex items-center gap-3 rounded-xl px-4 py-3"
                style={{ background: "rgba(18,21,25,0.7)", border: "1px solid rgba(255,255,255,0.07)" }}
              >
                <span
                  className="flex h-6 w-6 items-center justify-center rounded-full"
                  style={{ background: "rgba(245,158,11,0.14)", border: "1px solid rgba(245,158,11,0.3)" }}
                >
                  <Check size={13} className="text-amber-300" />
                </span>
                <span className="text-sm text-slate-200">{need}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Fretboard preview */}
        <div className="mt-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-400">
              {t("prepare.tapToHear", { zone: zoneA.label })}
            </h2>
            <ModeSelector mode={mode} onChange={setMode} compact />
          </div>
          <div className="h-40 sm:h-48">
            <Fretboard
              zone={zoneA}
              litNotes={mode === "FREE" ? zoneA.notes.filter((n) => n.role === "root") : zoneA.notes}
              mode={mode}
              compact
              onNoteTap={(n) => playFretNote(n.string, n.fret)}
            />
          </div>
        </div>

        {/* Mic (experimental) */}
        <div className="mt-6">
          <MicVisualizer testId="prepare-mic-btn" variant="test" />
        </div>

        {/* Actions */}
        <div className="mt-10 flex flex-col gap-3 sm:flex-row">
          <button
            data-testid="prepare-quick-practice-btn"
            onClick={() => navigate(`/jam/${song.id}`)}
            className="flex flex-1 items-center justify-center gap-2 rounded-full py-3.5 text-sm font-medium text-slate-200 transition active:scale-95"
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }}
          >
            <Hand size={16} /> {t("prepare.quickPractice")}
          </button>
          <button
            data-testid="prepare-skip-jam-btn"
            onClick={() => navigate(`/jam/${song.id}`)}
            className="flex flex-1 items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold text-[#1a1204] transition active:scale-95"
            style={{
              background: "linear-gradient(135deg,#FDE68A,#F59E0B 60%,#D97706)",
              boxShadow: "0 0 28px rgba(245,158,11,0.4)",
            }}
          >
            <Play size={16} fill="#1a1204" /> {t("prepare.skipJam")}
          </button>
        </div>
      </div>
    </div>
  );
}
